import { ChangeEvent, MouseEvent, ReactElement, useState } from "react";
import { AnnotationActions, AnnotationTypes } from "../../../shared/constants";
import { IAnnotationData } from "../../../shared/types";
import { getUniqueIdForAnnotation } from "../../../shared/utils";
import "./styles.scss";

interface IFlashcardPopup {
  onAnnotationResponse: (annotation: IAnnotationData) => void;
  toggleFlashcardPopup: (visibility: boolean) => void;
  annotation: IAnnotationData;
}

export const FlashcardPopup = ({
  annotation,
  onAnnotationResponse,
  toggleFlashcardPopup,
}: IFlashcardPopup): ReactElement => {
  const [frontText, setFrontText] = useState<string | undefined>(annotation.flashcardFrontText);
  const [backText, setBackText] = useState<string | undefined>(
    annotation.flashcardBackText || annotation.selectedText,
  );

  const isNewFlashcard = (): boolean => !annotation.flashcardFrontText;

  const handleFrontTextChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    setFrontText(event.target.value);
  };

  const handleBackTextChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    setBackText(event.target.value);
  };

  const onSave = (): void => {
    const updatedAnnotation = {
      ...annotation,
      flashcardFrontText: frontText,
      flashcardBackText: backText,
      annotationType: AnnotationTypes.FlashCard,
      actionType: isNewFlashcard() ? AnnotationActions.Add : AnnotationActions.Edit,
    };

    delete updatedAnnotation.color;
    delete updatedAnnotation.noteText;

    // New flashcards must not reuse the source highlight/note id — that would
    // replace the notebook entry and unwrap the highlight in the viewer.
    if (isNewFlashcard()) {
      updatedAnnotation.annotationId = getUniqueIdForAnnotation();
    }

    onAnnotationResponse(updatedAnnotation);
    toggleFlashcardPopup(false);
  };

  const onDelete = (): void => {
    annotation.actionType = AnnotationActions.Delete;
    onAnnotationResponse(annotation);
    toggleFlashcardPopup(false);
  };

  const hideNotesPopup = (event: MouseEvent): void => {
    event.preventDefault();

    if (event.target === event.currentTarget) {
      toggleFlashcardPopup(false);
    }
  };

  return (
    <div className="popup-container" onClick={hideNotesPopup}>
      <div className="flashcard-popup">
        <h4>
          {isNewFlashcard() ? <span>Create New Flashcard</span> : <span>Edit Flashcard</span>}
          <span
            className="close-button"
            onClick={(): void => {
              toggleFlashcardPopup(false);
            }}
          >
            Close
          </span>
        </h4>

        <label>Front:</label>
        <textarea
          placeholder="Front text"
          value={frontText}
          onChange={handleFrontTextChange}
        ></textarea>

        <label>Back:</label>
        <textarea
          placeholder="Back text"
          value={backText}
          onChange={handleBackTextChange}
        ></textarea>

        <div className="action-buttons">
          <button onClick={onSave} disabled={!frontText || !backText}>
            Save
          </button>

          {!isNewFlashcard() && <button onClick={onDelete}>Delete</button>}
        </div>
      </div>
    </div>
  );
};
